import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";
import { calculateDaysRemaining, getWarrantyStatus } from "../utils/warrantyUtils";
import AddWarrantyModal from "./AddWarrantyModal";
import { useNavigate } from "react-router-dom";
import { generateWarrantyReportPDF } from "../utils/generateWarrantyReportPDF";
import { generateWarrantyReportExcel } from "../utils/generateWarrantyReportExcel";

const WarrantyTracker = ({ clientId: propClientId, selectedProperty: propSelectedProperty, userInfo, readOnly = false }) => {
  const { selectedProperty: contextProperty, selectedClient: contextClient } = useContext(PropertyContext);
  const selectedProperty = propSelectedProperty || contextProperty;
  const clientId = propClientId || contextClient;
  const navigate = useNavigate();

  const [warranties, setWarranties] = useState([]);
  const [filteredWarranties, setFilteredWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [itemTypeFilter, setItemTypeFilter] = useState("All");
  const [itemTypes, setItemTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editWarranty, setEditWarranty] = useState(null);

  // Real-time listener for warranties
  useEffect(() => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }

    const warrantiesRef = propertyCollection(
      clientId,
      selectedProperty,
      "warranties"
    );
    const q = query(warrantiesRef, orderBy("purchaseDate", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const warrantyData = snapshot.docs.map((doc) => {
          const data = doc.data();
          const daysRemaining = calculateDaysRemaining(data.warrantyEndDate);
          const status = getWarrantyStatus(daysRemaining);

          return {
            id: doc.id,
            ...data,
            daysRemaining,
            isActive: daysRemaining >= 0,
            statusLabel: status.label,
            statusColor: status.color,
            chipColor: status.chipColor,
          };
        });

        setWarranties(warrantyData);

        // Extract unique item types for filter
        const types = [...new Set(warrantyData.map((w) => w.itemType).filter(Boolean))];
        setItemTypes(types.sort());

        setLoading(false);
      },
      (error) => {
        console.error("Error fetching warranties:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedProperty]);

  // Filter warranties based on search and filters
  useEffect(() => {
    let filtered = warranties;

    // Serial number search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (w) =>
          w.serialNumber?.toLowerCase().includes(term) ||
          w.itemName?.toLowerCase().includes(term) ||
          w.poNumber?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter === "Active") {
      filtered = filtered.filter((w) => w.isActive && w.daysRemaining > 30);
    } else if (statusFilter === "Expiring Soon") {
      filtered = filtered.filter((w) => w.daysRemaining >= 0 && w.daysRemaining <= 30);
    } else if (statusFilter === "Expired") {
      filtered = filtered.filter((w) => w.daysRemaining < 0);
    }

    // Item type filter
    if (itemTypeFilter !== "All") {
      filtered = filtered.filter((w) => w.itemType === itemTypeFilter);
    }

    setFilteredWarranties(filtered);
  }, [warranties, searchTerm, statusFilter, itemTypeFilter]);

  const handleAddClick = () => {
    setEditWarranty(null);
    setShowAddModal(true);
  };

  const handleEditClick = (warranty) => {
    setEditWarranty(warranty);
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditWarranty(null);
  };

  const handleViewDetail = (warrantyId) => {
    navigate(`/warranty-detail/${warrantyId}`);
  };

  const handleExportPDF = () => {
    const propertyName = selectedProperty || "Property";
    const reportType = statusFilter === "All" ? "All Warranties" : `${statusFilter} Warranties`;
    const filterInfo = {
      status: statusFilter,
      itemType: itemTypeFilter,
      searchTerm: searchTerm,
    };
    generateWarrantyReportPDF(
      filteredWarranties,
      propertyName,
      reportType,
      filterInfo,
      userInfo?.fullName || userInfo?.personalEmail || "Unknown"
    );
  };

  const handleExportExcel = async () => {
    const propertyName = selectedProperty || "Property";
    await generateWarrantyReportExcel(filteredWarranties, propertyName);
  };

  const handleManageTypes = () => {
    navigate("/warranty-types");
  };

  // Get summary stats
  const stats = {
    total: warranties.length,
    active: warranties.filter((w) => w.isActive && w.daysRemaining > 30).length,
    expiringSoon: warranties.filter((w) => w.daysRemaining >= 0 && w.daysRemaining <= 30).length,
    expired: warranties.filter((w) => w.daysRemaining < 0).length,
  };

  const columns = [
    {
      field: "serialNumber",
      headerName: "Serial Number",
      flex: 1.2,
      minWidth: 150,
    },
    {
      field: "itemName",
      headerName: "Item",
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: "itemType",
      headerName: "Type",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "poNumber",
      headerName: "PO #",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "purchaseDate",
      headerName: "Purchase Date",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "warrantyEndDate",
      headerName: "Warranty End",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "daysRemaining",
      headerName: "Days Left",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          sx={{
            color: params.row.statusColor,
            fontWeight: 600,
          }}
        >
          {params.row.daysRemaining < 0
            ? `${Math.abs(params.row.daysRemaining)} ago`
            : params.row.daysRemaining}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.statusLabel}
          color={params.row.chipColor}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetail(params.row.id)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!readOnly && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEditClick(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!selectedProperty) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Please select a property to view warranty tracker.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">Warranty Tracker</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!readOnly && (
            <>
              <Tooltip title="Manage Warranty Types">
                <IconButton onClick={handleManageTypes}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Button variant="contained" onClick={handleAddClick}>
                Add Warranty
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, p: 2, bgcolor: "primary.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="primary.main">
            {stats.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Warranties
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, bgcolor: "success.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="success.main">
            {stats.active}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, bgcolor: "warning.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="warning.main">
            {stats.expiringSoon}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Expiring Soon
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, bgcolor: "error.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="error.main">
            {stats.expired}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Expired
          </Typography>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Serial number, item, or PO #"
          sx={{ flex: 1, minWidth: 250 }}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            label="All"
            color={statusFilter === "All" ? "primary" : "default"}
            onClick={() => setStatusFilter("All")}
            clickable
          />
          <Chip
            label="Active"
            color={statusFilter === "Active" ? "success" : "default"}
            onClick={() => setStatusFilter("Active")}
            clickable
          />
          <Chip
            label="Expiring Soon"
            color={statusFilter === "Expiring Soon" ? "warning" : "default"}
            onClick={() => setStatusFilter("Expiring Soon")}
            clickable
          />
          <Chip
            label="Expired"
            color={statusFilter === "Expired" ? "error" : "default"}
            onClick={() => setStatusFilter("Expired")}
            clickable
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Item Type</InputLabel>
          <Select
            value={itemTypeFilter}
            label="Item Type"
            onChange={(e) => setItemTypeFilter(e.target.value)}
          >
            <MenuItem value="All">All Types</MenuItem>
            {itemTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Tooltip title="Export to PDF">
            <IconButton onClick={handleExportPDF}>
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton onClick={handleExportExcel}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredWarranties}
          columns={columns}
          loading={loading}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {/* Add/Edit Modal */}
      <AddWarrantyModal
        open={showAddModal}
        onClose={handleModalClose}
        clientId={clientId}
        selectedProperty={selectedProperty}
        userInfo={userInfo}
        editWarranty={editWarranty}
        existingWarranties={warranties}
      />
    </Container>
  );
};

export default WarrantyTracker;
